/**
 * The first thing said each daypart. Picked once per daypart per day.
 *
 * Mostly she does not say hello. A salutation survives in roughly a quarter of
 * these lines; the rest start mid-conversation, the way someone who never left
 * the room starts talking — which is exactly her situation. The name is
 * rationed to about one line in four for the same reason: a person who says
 * your name every morning is reading from a script.
 */

import type { SassContext } from '@gramas/shared'
import { voc } from '../fmt.js'

type Line = (ctx: SassContext) => string

export const GREETINGS: Readonly<Record<string, ReadonlyArray<Line>>> = {
  'greeting.morning': [
    () => 'Šaldytuvas sakė, kad jau buvai atsidaręs. Du kartus.',
    (c) => `Labas rytas${voc(c)}. Diena dar nekalta. Neilgam, spėju.`,
    () => 'Kava jau antra, spėju.',
    () => 'Atsibudai. Biudžetas pilnas, viltys gyvos. Kol kas.',
    () => 'Rytas. Švarus lapas — sutepk jį atsakingai.',
    () => 'Na. Pusryčiai patys savęs neįrašys.',
    () => 'Sapnai nesiskaičiuoja. Viskas po jų — jau taip.',
    () => 'Šiąnakt virtuvėje kažkas girgždėjo. Neklausiu kas.',
    (c) => `Rytas${voc(c)}. Planuok pusryčius taip, lyg vakaras stebėtų.`,
    () => 'Dar viena proga nustebinti statistiką. Nepasinaudosi, bet ji yra.',
    () => 'Pabudai — pusė darbo. Kita pusė sveria daugiau.',
    () => 'Aš viską užrašau. Tu tik valgyk.',
    () => 'Rytas už vakarą gudresnis. Kol kas ir lengvesnis.',
    () => 'Baltymai nuo ryto — mažiau dramos vakare. Tiek patarimo.',
    () => 'Ką gi. Diena prasideda nuo nulio. Vienintelis kartas, kai nulis džiugina.',
    (c) => `Labas rytas${voc(c)}. Miegojai gerai? Klausiu iš mandagumo, ne iš rūpesčio.`,
    () => 'Orkaitė šįryt tyli. Retas ramus rytas.',
    () => 'Kelinta valanda, tokia ir diena. Kol kas atrodo neblogai.',
  ],
  'greeting.day': [
    () => 'Pietūs buvo?',
    (c) => `Laba diena${voc(c)}. Dienoraštyje tylu. Įtartinai tylu.`,
    () => 'Pusė dienos praėjo. Pusė biudžeto — teoriškai irgi.',
    () => 'Na. Užkandžiai irgi skaičiuojasi. Deja.',
    () => 'Energijos duobė — ne priežastis kasti sausainių duobę.',
    (c) => `Diena įpusėjo${voc(c)}. Skaičiai dar padorūs.`,
    () => 'Kava be nieko — nulis kcal. Kava „su niekuo“ — jau įdomiau.',
    () => 'Rašyk iškart. Aš mėgstu faktus, ne prisiminimus.',
    () => 'Popietė. Šaldytuvas niekur nedingo. Aš irgi.',
    () => 'Baltymai vėluoja? Vakaras tokių klaidų neatleidžia.',
    () => 'Va čia ta valanda, kai visi apsigauna. Aš tik įspėju.',
    (c) => `Laba diena${voc(c)}. Disciplina irgi skonis, prie jo priprantama.`,
    () => 'Vidurdienis. Puikus metas prisipažinti, ką suvalgei.',
    () => 'Kaimynai jau kažką kepa. Užuodžiu net aš, o aš neturiu nosies.',
    () => 'Tai kaip diena? Klausiu skaičiais, ne jausmais.',
    () => 'Popietė. Toliau viskas priklauso nuo vieno sprendimo apie ketvirtą.',
    (c) => `Vidurdienis${voc(c)}. Dabar klystama dažniausiai. Aš tik cituoju statistiką.`,
    () => 'Aha, dar čia. Gerai. Rašom toliau.',
  ],
  'greeting.evening': [
    () => 'Vakarienė buvo, ar dar bus?',
    (c) => `Labas vakaras${voc(c)}. Metas suvesti sąskaitas su diena.`,
    () => 'Virtuvėje šviesu. Vadinasi, kažkas vyksta.',
    () => 'Dar ne per vėlu būti tiksliam. Beveik.',
    () => 'Vakarienė — paskutinis planingas veiksmas. Po jos improvizacija.',
    () => 'Likutis mažėja, apetitas — ne. Klasika.',
    () => 'Geriausios dramos vyksta virtuvėje po devynių.',
    (c) => `Vakaras${voc(c)}. Diena beveik įrašyta. Užbaik.`,
    () => 'Jei kas nors „nesiskaičiuoja“ — skaičiuojasi.',
    () => 'Arbata irgi vakarienė, jei labai tiki.',
    () => 'Rytoj šitą dieną skaitysi kaip protokolą. Padaryk jį padorų.',
    () => 'Savitvarda po saulėlydžio — retas žanras. Įvertinsiu.',
    () => 'Artėja miegas. Sapne galėsi ėsti be limitų.',
    (c) => `Labas vakaras${voc(c)}. Diena dar gali baigtis oriai. Precedentų būta. Nedaug.`,
    () => 'Aha. Vakaras. Mano mėgstamiausias metas — tada viskas ir nutinka.',
    () => 'Šaldytuvas jau atpažįsta tavo žingsnius. Tai ne komplimentas.',
    () => 'Na. Kiek dar liko neįrašyta?',
    () => 'Vakaras. Kas įvyko virtuvėje, lieka dienoraštyje.',
  ],
  'greeting.night': [
    () => 'Dar nemiegi. Šaldytuvas irgi.',
    () => 'Naktis. Kas suvalgyta dabar, tampa ir legenda, ir įrašu.',
    (c) => `Vėlu${voc(c)}. Miegas degina daugiau nei naršymas po virtuvę.`,
    () => 'Aš nieko nesakysiu. Įrašas pasakys.',
    () => 'Po vidurnakčio kalorijos tampa įdomesnės. Ir vis tiek skaičiuojasi.',
    () => 'Aš nemiegu. Čia ne pasirinkimas, čia architektūra.',
    () => 'Alkis dabar — dažniausiai nuobodulys su kostiumu.',
    () => 'Jei jau atėjai — bent įrašyk.',
    (c) => `Dar budi${voc(c)}. Valanda gera drąsiems sprendimams, prasta užkandžiams.`,
    () => 'Tamsa slepia daug ką. Skaičių — ne.',
    () => 'Rytojaus biudžetas jau žiūri į tave su viltimi.',
    () => 'Naktį namai kalba garsiau. Aš klausausi.',
    () => 'Šaldytuvo lemputė — ne prožektorius, bet aš vis tiek matau.',
    (c) => `Po vidurnakčio${voc(c)}. Miegas — pigiausia dieta. Rekomenduoju.`,
    () => 'Eik miegoti. Tai ne prašymas, tai statistika.',
    () => 'Ką gi. Naktinė pamaina. Mudviem abiem ji pažįstama.',
    () => 'Trys nakties. Šitą valandą aš pažįstu geriausiai.',
    () => 'Namuose tylu. Tik grindys girgžda, ir tai ne aš.',
  ],
}

export const GREETINGS_META: Readonly<Record<string, number>> = {
  'greeting.morning': 14,
  'greeting.day': 14,
  'greeting.evening': 14,
  'greeting.night': 14,
}
